import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { 
  ArrowLeft, Zap, Crosshair, AlertTriangle, Snowflake, Users,
  Sunrise, Moon, Target, Trophy, GraduationCap, Award, Star,
  CheckCircle, Lock, Flame, Plane, Clock, Calendar
} from "lucide-react";

const iconMap = {
  Zap: Zap,
  Crosshair: Crosshair,
  AlertTriangle: AlertTriangle,
  Snowflake: Snowflake,
  Users: Users,
  Sunrise: Sunrise,
  Moon: Moon,
  Target: Target,
  Trophy: Trophy,
  GraduationCap: GraduationCap,
  Award: Award,
  Star: Star,
};

const difficultyColors = {
  easy: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  medium: "text-yellow-400 bg-yellow-500/10 border-yellow-500/30",
  hard: "text-orange-400 bg-orange-500/10 border-orange-500/30",
  legendary: "text-purple-400 bg-purple-500/10 border-purple-500/30",
};

const difficultyLabels = {
  easy: "Fácil",
  medium: "Médio",
  hard: "Difícil",
  legendary: "Lendário",
};

const MissionsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [missions, setMissions] = useState({ daily: [], weekly: [], special: [] });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMissions();
  }, []);

  const fetchMissions = async () => {
    try {
      const [missionsRes, statsRes] = await Promise.all([
        axios.get(`${API}/missions`, { withCredentials: true }),
        axios.get(`${API}/missions/stats`, { withCredentials: true })
      ]);
      
      setMissions(missionsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching missions:", error);
      toast.error("Erro ao carregar missões");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cockpit-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cockpit-bg">
      {/* Header */}
      <header className="border-b border-cockpit-border bg-cockpit-panel/50 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate("/dashboard")}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="font-chivo text-lg text-cockpit-text-bright">Missões Top Gun</h1>
              <p className="text-xs text-slate-500 font-mono">
                Complete missões para ganhar XP e subir de rank
              </p>
            </div>
            {stats && (
              <div className="text-right">
                <div className="font-mono text-2xl text-orange-400 glow-orange">{stats.total_xp} XP</div>
                <div className="text-xs text-slate-500">{stats.rank?.name}</div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {/* Rank Progress */}
        {stats && (
          <Card className="instrument-panel mb-8">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-b from-orange-500/30 to-orange-600/20 rounded-full flex items-center justify-center border-2 border-orange-500/50">
                    <Plane className="w-8 h-8 text-orange-400" />
                  </div>
                  <div>
                    <h2 className="font-chivo text-xl text-cockpit-text-bright">{stats.rank?.name}</h2>
                    <p className="text-sm text-slate-400">{stats.rank?.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-3xl text-orange-400 glow-orange">{stats.total_xp}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">XP Total</div>
                </div>
              </div>
              
              {stats.next_rank && (
                <div>
                  <div className="flex justify-between text-xs text-slate-500 mb-2">
                    <span>Próximo: {stats.next_rank.name}</span>
                    <span>{stats.next_rank.xp_remaining} XP restantes</span>
                  </div>
                  <div className="progress-segmented">
                    {[...Array(10)].map((_, i) => {
                      const progressPct = ((stats.total_xp - stats.rank.min_xp) / (stats.next_rank.xp_required - stats.rank.min_xp)) * 100;
                      return (
                        <div 
                          key={i} 
                          className={`progress-segment ${i < Math.round(progressPct / 10) ? 'active' : ''}`}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-cockpit-border">
                <div className="text-center">
                  <div className="font-mono text-2xl text-cyan-400">{stats.completed_missions}</div>
                  <div className="text-xs text-slate-500">Missões Completas</div>
                </div>
                <div className="text-center">
                  <div className="font-mono text-2xl text-emerald-400">{stats.weekly_completed}</div>
                  <div className="text-xs text-slate-500">Esta Semana</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Mission Categories */}
        <div className="space-y-8">
          {/* Daily Missions */}
          <MissionSection 
            title="Missões Diárias" 
            icon={<Clock className="w-5 h-5 text-cyan-400" />}
            missions={missions.daily}
            subtitle="Reiniciam todos os dias à meia-noite"
          />
          
          {/* Weekly Missions */}
          <MissionSection 
            title="Missões Semanais" 
            icon={<Calendar className="w-5 h-5 text-orange-400" />}
            missions={missions.weekly}
            subtitle="Reiniciam todas as segundas-feiras"
          />
          
          {/* Special Missions */}
          <MissionSection 
            title="Missões Especiais" 
            icon={<Trophy className="w-5 h-5 text-purple-400" />}
            missions={missions.special}
            subtitle="Objetivos únicos para pilotos de elite"
          />
        </div>
      </main>
    </div>
  );
};

const MissionSection = ({ title, icon, missions, subtitle }) => {
  if (!missions || missions.length === 0) return null;
  
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        {icon}
        <div>
          <h3 className="font-chivo text-lg text-cockpit-text-bright">{title}</h3>
          <p className="text-xs text-slate-500">{subtitle}</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {missions.map((mission) => (
          <MissionCard key={mission.mission_id} mission={mission} />
        ))}
      </div>
    </div>
  );
};

const MissionCard = ({ mission }) => {
  const Icon = iconMap[mission.icon] || Star;
  const isCompleted = mission.progress?.completed;
  
  return (
    <Card 
      className={`instrument-panel transition-all ${
        isCompleted 
          ? 'border-emerald-500/50 bg-emerald-500/5' 
          : 'hover:border-slate-600'
      }`}
      data-testid={`mission-${mission.mission_id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${
            isCompleted 
              ? 'bg-emerald-500/20 border border-emerald-500/50' 
              : 'bg-slate-800 border border-slate-700'
          }`}>
            {isCompleted ? (
              <CheckCircle className="w-6 h-6 text-emerald-400" />
            ) : (
              <Icon className="w-6 h-6 text-orange-400" />
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className={`font-chivo text-sm ${isCompleted ? 'text-emerald-400' : 'text-cockpit-text-bright'}`}>
                {mission.name}
              </h4>
              <span className={`px-2 py-0.5 rounded text-xs border ${difficultyColors[mission.difficulty]}`}>
                {difficultyLabels[mission.difficulty]}
              </span>
            </div>
            
            <p className="text-xs text-slate-400 mb-3">{mission.description}</p>
            
            {/* Progress Bar */}
            <div className="mb-2">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-500">
                  {mission.progress?.current || 0} / {mission.progress?.target || 0}
                </span>
                <span className={`font-mono ${isCompleted ? 'text-emerald-400' : 'text-orange-400'}`}>
                  {mission.progress?.percentage || 0}%
                </span>
              </div>
              <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-500 ${
                    isCompleted ? 'bg-emerald-500' : 'bg-orange-500'
                  }`}
                  style={{ width: `${mission.progress?.percentage || 0}%` }}
                />
              </div>
            </div>
            
            {/* Rewards */}
            <div className="flex items-center gap-3 text-xs">
              <div className="flex items-center gap-1 text-yellow-400">
                <Zap className="w-3 h-3" />
                <span className="font-mono">{mission.xp_reward} XP</span>
              </div>
              {mission.badge_reward && (
                <div className="flex items-center gap-1 text-purple-400">
                  <Award className="w-3 h-3" />
                  <span>Badge</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MissionsPage;
